import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Lock from '@material-ui/icons/Lock';
import Avatar from '@material-ui/core/Avatar';
import withStyles from '@material-ui/core/styles/withStyles';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import { components as commonComponents } from '@555platform/portal-components/lib/client';
import {
  allPass, propEq, ifElse, isNil, map, find, prop, path, equals, reduce, and,
} from 'ramda';
import LoadingOverlay from 'react-loading-overlay';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import RoleHeader from './RoleHeader';
import Permissions from './Permissions';

const style = theme => ({
  gradientButton: {
    marginTop: '1rem',
    fontSize: '14px',
  },
  roleNameDivider: {
    marginTop: '1rem',
  },
  toggleRow: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 1rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '&:first-child': {
      flexBasis: '100%',
    },
  },
  switch: {
    textAlign: 'right',
    paddingTop: '0.8%',
  },
  toggleText: {
    paddingTop: '1.8%',
    marginLeft: '56%',
  },
});

const findPermission = (resource, action) => find(allPass([propEq('action', action), propEq('resource', resource)]));
const isActionEnabled = (action, permissions) => ifElse(isNil, () => ({ enabled: false, ...action }), () => ({ enabled: true, ...action }))(
  permissions,
);
const mapActionStates = (permissions, template) => map((mapping) => {
  const mappedActions = map(action => isActionEnabled(action, findPermission(mapping.resource, action.action)(permissions)))(mapping.actions);

  return {
    ...mapping,
    actions: mappedActions,
  };
})(template);

const getRoleIdentifier = path(['role', 'roleIdentifier']);
const getRoleDisplayName = prop('displayName');
const getRoleTeam = prop('team');

class RoleView extends Component {
  state = {
    displayName: '',
    team: '',
    checked: false,
    isActive: false,
  };

  permissionStatus = [];

  constructor(props) {
    super(props);
    this.handleToggleChange = this.handleToggleChange.bind(this);
  }


  static getDerivedStateFromProps(props, state) {
    const newRoleIdentifier = getRoleIdentifier(props);

    if (!equals(newRoleIdentifier, state.prevRoleIdentifier)) {
      return {
        prevRoleIdentifier: newRoleIdentifier,
        displayName: getRoleDisplayName(newRoleIdentifier),
        team: getRoleTeam(newRoleIdentifier),
      };
    }
    return null;
  }

  onHeaderChange = (type, value) => {
    switch (type) {
      case 'name':
        this.setState({ displayName: value });
        break;

      case 'id':
        this.setState({ team: value });
        break;
      case 'checked':
        this.setState({ checked: value });
        break;
      default:
        break;
    }
  };

  onReceive(updateAllPermissions) {
    this.updateAllPermissions = updateAllPermissions;
  }

  evaluateResult = (result) => {
    const _this = this;
    return result.reduce(
      (accumulator, promise) => promise.then((values) => {
          const checked = values.map((value) => {
            const enabled = Object.values(value.data);
            return enabled[0].allowed;
          });
          if (reduce(and, true, checked)) {
            return _this.permissionStatus.push(true);
          } 
            return _this.permissionStatus.push(false);
          
        }),
      true,
    );
  };

  handleToggleChange = () => (e) => {
    const _this = this;
    const eventTarget = e.target.checked;
    this.setState({ isActive: true });
    this.permissionStatus.splice(0, this.permissionStatus.length);
    const result = this.updateAllPermissions(e.target.checked);
    this.evaluateResult(result)
      .then((values) => {
        if (reduce(and, true, this.permissionStatus) === eventTarget) {
          _this.setState({ checked: eventTarget });
        }
        _this.setState({ isActive: false });
      })
      .catch((error) => {
        console.error('Error occurred while setting  all permissions', error);
      });
  };

  updateAllPermissions() {}

  render() {
    const {
      title,
      role,
      template,
      classes,
      isLoading,
      readOnly,
      onPermissionChange,
      onRoleIdentifierChange,
      history,
    } = this.props;
    const { roleIdentifier, permissions } = role;
    const { name } = roleIdentifier;
    const actualTeam = roleIdentifier.team;
    const actualDisplayName = roleIdentifier.displayName;
    const { displayName, team } = this.state;
    return (
      <commonComponents.Panel
        title={`${title}: ${displayName}`}
        avatar={(
<Avatar>
            <Lock />
          </Avatar>
)}
      >
        {!isLoading && (
          <div>
            <RoleHeader
              name={displayName}
              team={team}
              readOnly={readOnly}
              onChange={this.onHeaderChange}
            />
            <LoadingOverlay
              active={this.state.isActive}
              spinner
              text=" Loading your content"
              styles={{
                overlay: base => ({
                  ...base,
                  background: 'rgba(0, 0, 0, 0)',
                }),
                spinner: base => ({
                  ...base,
                  width: '30%',
                  height: '120%',
                  marginRight: '-170px',
                  '& svg circle': {
                    stroke: 'rgba(5, 5, 5, 1)',
                  },
                }),
              }}
            >
              <div className={classes.toggleRow}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.gradientButton}
                  onClick={() => onRoleIdentifierChange(name, team)}
                  disabled={readOnly}
                >
                  Update Role Name
                </Button>
                <Typography className={classes.toggleText} variant="subheading">
                  Select All Permissions :
                  {' '}
                </Typography>
                <Switch
                  color="primary"
                  className={classes.switch}
                  checked={this.state.checked}
                  onChange={this.handleToggleChange()}
                />
              </div>
            </LoadingOverlay>
            <Divider className={classes.roleNameDivider} />
          </div>
        )}
        <Permissions
          getUpdateAllPermissions={this.onReceive.bind(this)}
          template={mapActionStates(permissions, template)}
          onChange={onPermissionChange({
            name,
            team: actualTeam,
            displayName: actualDisplayName,
          })}
          isLoading={isLoading}
          readOnly={readOnly}
        />
        <Button
          variant="contained"
          className={classes.gradientButton}
          onClick={() => {
            history.goBack();
          }}
        >
          Back
        </Button>
      </commonComponents.Panel>
    );
  }
}

RoleView.propTypes = {
  title: PropTypes.string.isRequired,
  role: PropTypes.object.isRequired,
  template: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onPermissionChange: PropTypes.func.isRequired,
  onRoleIdentifierChange: PropTypes.func.isRequired,
};

export default withStyles(style)(RoleView);
